import React, { useState } from 'react';
import SystemDbUpdateView from './SystemDbUpdateView';
import NaverGridMap from './NaverGridMap';

const ESS_STATIC_MAP_PATH = '/ess-project/index.html';
const ESS_STATIC_TEST_MAP_PATH = '/ess-project/통합_변전소_배전선로_네이버지도.html';

const PROJECT_DETAIL_META = {
  systemDbUpdate: {
    description: '한전 직접 수집, 실행 로그 확인, 계통 DB 데이터 탐색을 한 화면에서 관리합니다.',
  },
  gridMapEss: {
    description: '변전소와 배전선로를 선택하여 정적 HTML 프리뷰로 배전망 ESS 데이터를 확인합니다.',
  },
  gridMapEssDynamic: {
    description: '변전소와 배전선로를 선택하여 동적 네이버 지도에서 배전망 ESS 데이터를 확인합니다.',
  },
  gridMapEssStaticTest: {
    description: '기존 변전소-배전선로 정적 HTML 데이터를 하나로 통합하여 전체 배전망 ESS 후보 좌표를 확인합니다.',
  },
  gridMapCapacity: {
    description: '변전소와 배전선로를 선택하고 용량기준으로 필터링하여 네이버 지도에서 계통 여유용량 데이터를 확인합니다.',
  },
};

function ProjectDetailHeader({ project, onBack, description }) {
  return (
    <div className="dashboard-section-header">
      <div>
        <p className="dashboard-mini-label">PROJECT DETAIL</p>
        <h2 className="dashboard-section-title dashboard-project-title">
          <span>{project.name}</span>
          {project.variantLabel && (
            <span className="dashboard-project-variant-badge">
              {project.variantLabel}
            </span>
          )}
        </h2>
        <p className="dashboard-section-description">{description}</p>
      </div>

      <div>
        <button className="dashboard-primary-btn" onClick={onBack}>
          Back to Projects
        </button>
      </div>
    </div>
  );
}

function EssStaticProjectView({
  mapPath = ESS_STATIC_MAP_PATH,
  title = '배전망 ESS Static Preview',
  description = '`public/ess-project/index.html` 기준 정적 페이지를 아래 iframe 프리뷰에서 바로 확인할 수 있습니다.',
  resetLabel = '지도 선택으로 돌아가기',
}) {
  const [frameVersion, setFrameVersion] = useState(0);

  const resetStaticPreview = () => {
    setFrameVersion((current) => current + 1);
  };

  return (
    <div className="dashboard-panel dashboard-panel-wide">
      <div
        className="dashboard-panel-header"
        style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '18px' }}
      >
        <h3>{title}</h3>
        <button
          type="button"
          className="dashboard-secondary-btn"
          style={{ alignSelf: 'flex-end' }}
          onClick={resetStaticPreview}
        >
          {resetLabel}
        </button>
      </div>
      <p className="dashboard-section-description" style={{ marginBottom: '16px' }}>
        {description}
      </p>
      <div className="ess-static-frame-wrap">
        <iframe
          key={frameVersion}
          title={title}
          src={mapPath}
          className="ess-static-frame"
        />
      </div>
    </div>
  );
}

export default function ProjectDetailView({ project, onBack }) {
  if (!project) {
    return (
      <div className="dashboard-panel">
        <h3>Project not found</h3>
        <button className="dashboard-primary-btn" onClick={onBack}>
          Back to Projects
        </button>
      </div>
    );
  }

  const detailMeta = PROJECT_DETAIL_META[project.viewType];

  if (project.viewType === 'systemDbUpdate') {
    return (
      <>
        <ProjectDetailHeader project={project} onBack={onBack} description={detailMeta.description} />
        <SystemDbUpdateView />
      </>
    );
  }

  if (project.viewType === 'gridMapEss') {
    return (
      <>
        <ProjectDetailHeader project={project} onBack={onBack} description={detailMeta.description} />
        <EssStaticProjectView />
      </>
    );
  }

  if (project.viewType === 'gridMapEssStaticTest') {
    return (
      <>
        <ProjectDetailHeader project={project} onBack={onBack} description={detailMeta.description} />
        <EssStaticProjectView
          mapPath={ESS_STATIC_TEST_MAP_PATH}
          title="배전망 ESS Static + Test Preview"
          description="기존 변전소-배전선로 HTML 40개 파일의 좌표를 하나로 통합한 테스트 지도를 아래 iframe 프리뷰에서 확인할 수 있습니다."
          resetLabel="통합 지도 다시 불러오기"
        />
      </>
    );
  }

  if (project.viewType === 'gridMapEssDynamic') {
    return (
      <>
        <ProjectDetailHeader project={project} onBack={onBack} description={detailMeta.description} />
        <div className="dashboard-panel dashboard-panel-wide">
          <NaverGridMap source="ess" />
        </div>
      </>
    );
  }

  if (project.viewType === 'gridMapCapacity') {
    return (
      <>
        <ProjectDetailHeader project={project} onBack={onBack} description={detailMeta.description} />
        <div className="dashboard-panel dashboard-panel-wide">
          <NaverGridMap source="capacity" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="dashboard-section-header">
        <div>
          <p className="dashboard-mini-label">PROJECT DETAIL</p>
          <h2 className="dashboard-section-title dashboard-project-title">
            <span>{project.name}</span>
            {project.variantLabel && (
              <span className="dashboard-project-variant-badge">
                {project.variantLabel}
              </span>
            )}
          </h2>
          <p className="dashboard-section-description">
            {project.summary || 'Review the core information, current progress, and next actions for this project.'}
          </p>
        </div>

        <div>
          <button className="dashboard-primary-btn" onClick={onBack}>
            Back to Projects
          </button>
        </div>
      </div>

      <div className="dashboard-detail-grid">
        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <h3>Overview</h3>
          </div>

          <div className="detail-item">
            <span className="detail-label">Project Name</span>
            <span className="detail-value">
              {project.name}
              {project.variantLabel ? ` (${project.variantLabel})` : ''}
            </span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Category</span>
            <span className="detail-value">{project.category}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Status</span>
            <span className="detail-value">{project.status}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Owner</span>
            <span className="detail-value">{project.owner}</span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Timeline</span>
            <span className="detail-value">{project.timeline}</span>
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <h3>Milestones</h3>
          </div>
          <ul className="dashboard-list">
            {(project.milestones || []).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="dashboard-panel dashboard-panel-wide">
          <div className="dashboard-panel-header">
            <h3>Next Actions</h3>
          </div>
          <ul className="dashboard-list">
            {(project.nextActions || []).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
